import { tenantAssetUrl } from "./utils/tenantAssetUrl";

type Theme = {
  backgroundColor: string;
  headerColor: string;
  headerTextColor: string;
  boxBackgroundColor: string;
  buttonColor: string;
  buttonDisabledColor: string;
  buttonTextColor: string;
  textColor: string;
  colorAccent1: string;
  favicon: string;
  logo192: string;
  logo512: string;
  logoHeader: string;
};

export type { Theme };

export const themes: Record<string, Theme> = {
  main: {
    backgroundColor: "#F2ECE2",
    headerColor: "#F2ECE2",
    headerTextColor: "#000",
    boxBackgroundColor: "#fff",
    buttonColor: "#000",
    buttonDisabledColor: "#555",
    buttonTextColor: "#fff",
    textColor: "#000",
    colorAccent1: "#4d813c",
    favicon: "/favicon.ico",
    logo192: "/logo192.png",
    logo512: "/logo512.png",
    logoHeader: "",
  },
  black: {
    backgroundColor: "#000",
    headerColor: "#000",
    headerTextColor: "#fff",
    boxBackgroundColor: "#fff",
    buttonColor: "#000",
    buttonDisabledColor: "#555",
    buttonTextColor: "#fff",
    textColor: "#000",
    colorAccent1: "#4d813c",
    favicon: "/favicon-black.ico",
    logo192: "/women-nyc-logo192.png",
    logo512: "/women-nyc-logo512.png",
    logoHeader: "",
  },
  "tenant-werule-demo": {
    backgroundColor: "#F2ECE2",
    headerColor: "#F2ECE2",
    headerTextColor: "#000000",
    boxBackgroundColor: "#FFFFFF",
    buttonColor: "#000000",
    buttonDisabledColor: "#555555",
    buttonTextColor: "#FFFFFF",
    textColor: "#000000",
    colorAccent1: "#4d813c",
    favicon: "/favicon.ico",
    logo192: "/logo192.png",
    logo512: "/logo512.png",
    logoHeader: "",
  },
  "tenant-take-the-lead": {
    backgroundColor: "#F6F6F6",
    headerColor: "#F6F6F6",
    headerTextColor: "#000000",
    boxBackgroundColor: "#FFFFFF",
    buttonColor: "#000000",
    buttonDisabledColor: "#555555",
    buttonTextColor: "#FFFFFF",
    textColor: "#000000",
    colorAccent1: "#da4331",
    favicon: "/tenants/take-the-lead/favicon.ico",
    logo192: "/tenants/take-the-lead/logo192.png",
    logo512: "/tenants/take-the-lead/logo512.png",
    logoHeader: "/tenants/take-the-lead/logoHeader.png",
  },
};

export const buildTimeThemeName = process.env.REACT_APP_THEME || "main";

/** Best-effort tenant theme from hostname before /config returns (avoids tan→tenant color flash). */
export function inferThemeKeyFromHostname(hostname: string = typeof window !== "undefined" ? window.location.hostname : ""): string | null {
  const host = hostname.toLowerCase();
  // Prod is takethelead.we-rule.com (no hyphens); test uses take-the-lead-test…
  if (host.includes("take-the-lead") || host.includes("takethelead")) return "tenant-take-the-lead";
  if (host.includes("mt-demo") || host.includes("werule-demo")) return "tenant-werule-demo";
  if (host.includes("women.nyc") || host.includes("womennyc")) return "black";
  if (host.includes("test.weruledev.com")) return "black";
  return null;
}

const PLATFORM_BRAND = "WERULE";

/** Browser tab title — matches server link-preview copy for tenant sites. */
export function documentTitleForBrand(brandName: string, tenantBranded = true): string {
  const brand = brandName.trim();
  if (!tenantBranded || !brand) {
    return PLATFORM_BRAND;
  }
  return `${brand}, powered by ${PLATFORM_BRAND}`;
}

export function inferBrandNameFromHostname(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : ""
): string | null {
  const host = hostname.toLowerCase();
  if (host.includes("take-the-lead") || host.includes("takethelead")) return "Take The Lead";
  if (host.includes("mt-demo") || host.includes("werule-demo")) return "WERULE DEMO";
  if (host.includes("women.nyc") || host.includes("womennyc")) return "WOMEN.NYC";
  if (host.includes("test.weruledev.com")) return "WOMEN.NYC";
  return null;
}

export function applyDocumentTitleFromHostname() {
  if (typeof document === "undefined") {
    return;
  }
  const inferredBrand = inferBrandNameFromHostname();
  document.title = inferredBrand
    ? documentTitleForBrand(inferredBrand)
    : PLATFORM_BRAND;
}

export function applyDocumentTitleForBrand(brandName?: string | null) {
  if (typeof document === "undefined") {
    return;
  }
  const brand = brandName?.trim();
  if (brand) {
    document.title = documentTitleForBrand(brand);
    return;
  }
  applyDocumentTitleFromHostname();
}

export function bootstrapThemeFromHostname() {
  const inferred = inferThemeKeyFromHostname();
  if (inferred && themes[inferred]) {
    applyRuntimeTheme(inferred);
    return inferred;
  }
  applyRuntimeTheme(buildTimeThemeName);
  return buildTimeThemeName;
}

/** Prefer hostname tenant theme when API omits theme or sends the wrong key for this host. */
export function resolveThemeKeyFromConfig(apiThemeKey?: string | null): string {
  const inferred = inferThemeKeyFromHostname();
  const apiKey = apiThemeKey?.trim();
  if (inferred) {
    if (apiKey && apiKey === inferred) {
      return apiKey;
    }
    return inferred;
  }
  if (apiKey) {
    return apiKey;
  }
  return buildTimeThemeName;
}

/** CSS custom properties set on documentElement by applyRuntimeTheme. */
export const THEME_CSS_VARS: Record<keyof Theme, string> = {
  backgroundColor: "--wr-background-color",
  headerColor: "--wr-header-color",
  headerTextColor: "--wr-header-text-color",
  boxBackgroundColor: "--wr-box-background-color",
  buttonColor: "--wr-button-color",
  buttonDisabledColor: "--wr-button-disabled-color",
  buttonTextColor: "--wr-button-text-color",
  textColor: "--wr-text-color",
  colorAccent1: "--wr-color-accent-1",
  favicon: "--wr-favicon",
  logo192: "--wr-logo192",
  logo512: "--wr-logo512",
  logoHeader: "--wr-logo-header",
};

let runtimeThemeKey: string | null = null;
let runtimeThemeOverride: Theme | null = null;

function applyThemeCssVars(theme: Theme) {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  (Object.keys(THEME_CSS_VARS) as (keyof Theme)[]).forEach((key) => {
    const value = theme[key];
    if (value) {
      root.style.setProperty(THEME_CSS_VARS[key], value);
    }
  });
}

function normalizeHex(color?: string): string {
  return (color || "").trim().toUpperCase();
}

/** Merge wizard/API branding; keep base header/box contrast only when branding omits those fields. */
export function mergeThemeBranding(base: Theme, branding: Partial<Theme>): Theme {
  const merged: Theme = { ...base, ...branding };

  const assetKeys: (keyof Theme)[] = ["favicon", "logo192", "logo512", "logoHeader"];
  for (const key of assetKeys) {
    const value = branding[key];
    if (typeof value !== "string" || value.trim() === "") {
      merged[key] = base[key];
    }
  }

  const brandingBg = normalizeHex(branding.backgroundColor);
  const brandingHeader = normalizeHex(branding.headerColor);
  const brandingBox = normalizeHex(branding.boxBackgroundColor);
  const headerExplicit = branding.headerColor !== undefined && branding.headerColor !== "";
  const boxExplicit = branding.boxBackgroundColor !== undefined && branding.boxBackgroundColor !== "";

  if (
    !headerExplicit &&
    brandingBg &&
    brandingHeader &&
    brandingHeader === brandingBg &&
    normalizeHex(base.headerColor) !== normalizeHex(base.backgroundColor)
  ) {
    merged.headerColor = base.headerColor;
  }

  if (
    !boxExplicit &&
    brandingBg &&
    brandingBox &&
    brandingBox === brandingBg &&
    normalizeHex(base.boxBackgroundColor) !== normalizeHex(base.backgroundColor)
  ) {
    merged.boxBackgroundColor = base.boxBackgroundColor;
  }

  return merged;
}

export function applyRuntimeTheme(
  themeKey: string,
  branding?: Partial<Theme>,
  brandingAssetVersion?: number
) {
  runtimeThemeKey = themeKey;
  const base = themes[themeKey] || themes[buildTimeThemeName] || themes.main;
  runtimeThemeOverride = branding ? mergeThemeBranding(base, branding) : base;

  applyThemeCssVars(runtimeThemeOverride);

  if (typeof document !== "undefined") {
    document.body.classList.toggle("theme-black", themeKey === "black");

    const favicon = runtimeThemeOverride.favicon;
    if (favicon) {
      document.querySelectorAll("link[rel*='icon']").forEach((node) => {
        const link = node as HTMLLinkElement;
        if (link.rel === "apple-touch-icon") {
          return;
        }
        link.href = tenantAssetUrl(favicon, brandingAssetVersion);
      });
    }

    const touchIcon = runtimeThemeOverride.logo192;
    if (touchIcon) {
      const appleLink = document.querySelector(
        'link[rel="apple-touch-icon"]'
      ) as HTMLLinkElement | null;
      if (appleLink) {
        appleLink.href = tenantAssetUrl(touchIcon, brandingAssetVersion);
      }
    }
  }
}

export function getCurrentThemeName(): string {
  return runtimeThemeKey || buildTimeThemeName;
}

/** Auth/login logo variant: NYC artwork only for the NYC (`black`) theme. */
export function getAuthLogoType(): "main" | "women-nyc-black" | "tenant-brand" {
  if (getCurrentThemeName() === "black") return "women-nyc-black";
  const { logo512, logo192, logoHeader } = getCurrentTheme();
  if (
    logoHeader?.startsWith("/tenants/") ||
    logo512?.startsWith("/tenants/") ||
    logo192?.startsWith("/tenants/")
  ) {
    return "tenant-brand";
  }
  return "main";
}

export function getCurrentTheme(): Theme {
  if (runtimeThemeOverride) {
    return runtimeThemeOverride;
  }
  return themes[buildTimeThemeName] || themes.main;
}

/** @deprecated use getCurrentThemeName() for runtime-aware theme key */
export const currentThemeName = buildTimeThemeName;

/** @deprecated use getCurrentTheme() for runtime-aware theme */
export const currentTheme = themes[buildTimeThemeName] || themes.main;
